// Channel types for CEP
export enum ChannelType {
  Email = "email",
  Sms = "sms",
  Whatsapp = "whatsapp",
  Push = "push",
  InApp = "in_app",
}

/**
 * The raw string form of {@link ChannelType} — what a `channel_type` column or
 * an SQS payload carries. Enum members are assignable to it, so the predicates
 * below take it and one copy of each rule serves both spellings.
 */
export type ChannelTypeValue = "email" | "sms" | "whatsapp" | "push" | "in_app";

/**
 * Whether messages on this channel are sent from a sender identity. Push and
 * in-app are delivered through the customer's own app, so they have no sender
 * to configure and no "From" to show.
 */
export function channelHasSenderIdentity(
  channelType: ChannelTypeValue,
): boolean {
  return (
    channelType === ChannelType.Email ||
    channelType === ChannelType.Sms ||
    channelType === ChannelType.Whatsapp
  );
}

/**
 * Whether this channel has consent semantics: subscription topics a recipient
 * can unsubscribe from, and therefore a marketing/transactional distinction.
 * In-app messages sit in an in-product inbox the user dismisses, so they carry
 * neither, and their `transactional` stays null.
 */
export function channelHasConsentModel(channelType: ChannelTypeValue): boolean {
  return channelType !== ChannelType.InApp;
}

/**
 * Whether a message on this channel carries any editable message-level
 * metadata. Derived from the fields the metadata surface actually renders, so
 * a channel that later gains one of them stops reading as empty on its own.
 */
export function channelHasMessageMetadata(
  channelType: ChannelTypeValue,
): boolean {
  return (
    // Subject and preheader.
    channelType === ChannelType.Email ||
    // Message type and subscription topic.
    channelHasConsentModel(channelType) ||
    // Sender identity and reply-to.
    channelHasSenderIdentity(channelType)
  );
}

/**
 * Channels whose channel-level unsubscribe arrives as a reply keyword (STOP),
 * making it an absolute opt-out with no transactional carve-out.
 *
 * Those channels' sentinels are read under more than one policy: opt-out for most
 * messages, opt-in for transactional sends.
 */
export const REPLY_KEYWORD_STOP_CHANNELS: ReadonlySet<ChannelTypeValue> =
  new Set([ChannelType.Sms, ChannelType.Whatsapp]);
